import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Language = "en" | "hi" | "gu";

const JL_LANG_KEY = "jl_ui_language";

function readStoredLanguage(): Language {
  try {
    const v = localStorage.getItem(JL_LANG_KEY);
    if (v === "en" || v === "hi" || v === "gu") {
      return v;
    }
  } catch {
    /* noop */
  }
  return "en";
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

function interpolate(
  template: string,
  vars?: Record<string, string | number>
): string {
  if (!vars) {
    return template;
  }
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(`{{${k}}}`, String(v)),
    template
  );
}

/** All UI strings for the public Jain Lexicon site (EN / HI / GU). */
const translations: Record<Language, Record<string, string>> = {
  en: {
    "site.title": "Jain Lexicon",
    "site.subtitle": "A comprehensive dictionary of Jain terminology",
    "nav.home": "Home",
    "nav.browse": "Browse",
    "nav.contribute": "Contribute",
    "nav.about": "About",
    "nav.search": "Search",
    "header.login": "Login",
    "header.language": "Language",
    "header.myAccount": "My account",
    "header.accountPassword": "Account & password",
    "header.logout": "Logout",
    "header.admin": "Admin",
    "header.adminTooltip": "Dashboard",
    "header.adminPortal": "Admin portal",
    "header.menuMyAccount": "My Account",
    "header.resetPassword": "Reset password",
    "header.openMenu": "Open menu",
    "header.closeMenu": "Close menu",
    "header.myContributions": "My Contributions",
    "header.mainNav": "Main navigation",
    "search.placeholder": "Search for a term...",
    "search.button": "Search",
    "search.title": "Search Results",
    "search.resultsFor": "Results for \"{{query}}\"",
    "search.backToBrowse": "Back to Browse",
    "search.foundSingular": "Found {{count}} result",
    "search.foundPlural": "Found {{count}} results",
    "search.loadErrorTitle": "Unable to load results",
    "search.loadErrorBody":
      "There was an issue connecting to the database. Please try again.",
    "search.noResults": "No results found",
    "search.noResultsHint":
      "Try searching with different keywords or browse alphabetically",
    "search.browseAll": "Browse All Terms",
    "alphabetical.title": "Browse Alphabetically",
    "browse.subtitle":
      "Explore the complete lexicon of Jain terminology organized alphabetically",
    "browse.filteringByLetter": "Filtering by letter",
    "browse.termsStartingWith": "Terms starting with \"{{letter}}\"",
    "browse.noTermsLetter": "No terms found starting with \"{{letter}}\"",
    "browse.noTermsGeneric": "No terms found",
    "browse.tryDifferentLetter":
      "Try selecting a different letter or be the first to contribute a term!",
    "browse.emptyGeneric": "Be the first to contribute to the Jain Lexicon!",
    "browse.perPage": "Per page",
    "browse.paginationPrev": "Previous",
    "browse.paginationNext": "Next",
    "browse.paginationPageOf": "Page {{current}} of {{total}}",
    "browse.paginationRange": "{{start}}–{{end}} of {{total}}",
    "term.meaning": "Meaning",
    "term.pronunciation": "Pronunciation",
    "term.hindi": "Hindi",
    "term.gujarati": "Gujarati",
    "term.english": "English",
    "termCard.viewTerm": "View term",
    "termCard.viewOneLanguage": "View {{count}} language",
    "termCard.viewAllLanguages": "View all {{count}} languages",
    "termCard.exactMatch": "Exact Match",
    "termDetail.backToBrowse": "Back to Browse",
    "termDetail.notFound": "Term Not Found",
    "termDetail.notFoundBody":
      "The term you're looking for doesn't exist or has been removed.",
    "termDetail.browseAllTerms": "Browse All Terms",
    "termDetail.exampleUsage": "Example Usage",
    "termDetail.quickInfo": "Quick Info",
    "termDetail.firstLetter": "First Letter",
    "termDetail.languagesLabel": "Languages",
    "termDetail.relatedTerms": "Related Terms",
    "termDetail.recordedPronunciation": "Recorded pronunciation",
    "termDetail.audioPlay": "Play pronunciation",
    "termDetail.audioPause": "Pause pronunciation",
    "termDetail.audioMute": "Mute audio",
    "termDetail.audioUnmute": "Unmute audio",
    "termDetail.linkCopied": "Link copied to clipboard!",
    "termDetail.shareText": "Learn about {{word}} - Jain Lexicon",
    "wotd.featured": "Featured",
    "wotd.title": "Word of the Day",
    "wotd.exploreFull": "Explore Full Definition",
    "contribute.title": "Contribute a New Term",
    "contribute.description":
      "Help expand our lexicon by submitting new Jain terms",
    "contribute.word": "Word / Term",
    "contribute.meaningHindi": "Meaning in Hindi",
    "contribute.meaningGujarati": "Meaning in Gujarati",
    "contribute.pronunciation": "Pronunciation (optional)",
    "contribute.example": "Example Usage (optional)",
    "contribute.email": "Your Email",
    "contribute.contributorEmail": "Contributor email",
    "contribute.yourInfo": "Your information *",
    "contribute.identityFromProfile": "Taken from your account profile.",
    "contribute.name": "Your Name",
    "contribute.submit": "Submit Contribution",
    "contribute.success":
      "Thank you! Your contribution has been submitted for review.",
    "contribute.guidelinesBadge": "Contribution Guidelines",
    "contribute.g1Title": "Authentic Terms",
    "contribute.g1Desc":
      "Ensure the term is related to Jain philosophy, practices, or scriptures.",
    "contribute.g2Title": "Accurate Meanings",
    "contribute.g2Desc":
      "Provide accurate meanings in at least one language (Hindi or Gujarati).",
    "contribute.g3Title": "Review Process",
    "contribute.g3Desc":
      "All submissions are reviewed by experts before publication.",
    "contribute.g4Title": "Quality Standards",
    "contribute.g4Desc":
      "Include pronunciation guides when possible to help learners.",
    "contribute.myContributions": "My contributions",
    "contribute.trackStatus": "Track the status of terms you have submitted.",
    "contribute.showingFor": "Showing submissions for",
    "contribute.submitNew": "Submit a new term",
    "contribute.submittingAs": "Submitting as:",
    "contribute.successReviewBody":
      "Your term will be reviewed by our team before being published.",
    "contribute.submitAnother": "Submit Another Term",
    "contribute.viewMyContributions": "View my contributions",
    "contribute.wordPlaceholder": "e.g., Ahimsa, Anekantavada",
    "contribute.meaningEnglishPlaceholder": "Write meaning in English...",
    "contribute.pronunciationPlaceholder": "e.g., suhm-yuk",
    "contribute.examplePlaceholder":
      "How the term is used in context...",
    "hero.title": "Explore the Sacred",
    "hero.titleAccent": "Language of Jainism",
    "hero.description":
      "Discover the profound meanings behind Jain terminology in Hindi and Gujarati",
    "index.heroTagline": "Sacred Knowledge • Timeless Wisdom",
    "quickLinks.searchTitle": "Search Terms",
    "quickLinks.searchDesc":
      "Find any Jain terminology instantly with our powerful search",
    "quickLinks.browseTitle": "Browse Lexicon",
    "quickLinks.browseDesc":
      "Explore all terms organized alphabetically",
    "quickLinks.contributeDesc":
      "Help expand the lexicon by adding new terms",
    "quickLinks.exploreTitle": "Explore",
    "quickLinks.exploreDesc":
      "Discover sacred knowledge and wisdom",
    "index.creditsAdvisor": "Academic Advisor",
    "index.creditsEditor": "General Editor",
    "index.addTerm": "Add Term",
    "index.quickAccess": "Quick Access",
    "index.featuredTerms": "Featured Terms",
    "index.featuredSubtitle": "Explore the wisdom of Jain philosophy",
    "index.viewAllTerms": "View All Terms",
    "index.showMoreFeatured": "Show more",
    "index.showLessFeatured": "Show less",
    "common.loadErrorTitle": "Unable to load terms",
    "common.loadErrorBody":
      "There was an issue loading terms from the service. Please try again.",
    "common.retry": "Retry",
    "index.noTermsTitle": "No terms yet",
    "index.noTermsBody":
      "Be the first to contribute to the Jain Lexicon!",
    "common.addTerm": "Add a Term",
    "index.purposeEyebrow": "Jain Lexicon",
    "index.purposeTitle": "Our Purpose",
    "index.purposeSubtitle":
      "A modern, heritage-inspired platform for Jain terminology, pronunciation, and multilingual learning.",
    "index.mission": "Our Mission",
    "index.missionLead":
      "Explain Jain terminologies with clarity, reverence, and tools that support real study.",
    "index.missionBullets":
      "Multimedia entries with pronunciation guides, images, and rich context|Authoritative English meanings with standardized diacritical spellings|Hindi and Gujarati explanations alongside every term|Built for students, scholars, and the wider Jain community",
    "index.missionBody":
      "Jain Lexicon is a multimedia platform designed to explain Jain terminologies with proper pronunciation guide and meanings in Hindi and Gujarati.",
    "index.vision": "Our Vision",
    "index.visionLead": "",
    "index.visionBullets":
      "Jainlexicon.org is a multimedia platform designed to explain Jain terminologies with proper pronunciation guide and pictures etc.|This is a collaborative project that will always be in a constant state of \"Work in progress\". This site is a learning tool for studying Jainism.|You are not only invited to browse but also to contribute. Any user can suggest changes or add new terms to make the site more useful to students and scholars of Jainism.|Let's concentrate on preparing the English version first. It is critical to define definitive English spellings of Jain words. Presently, we see words such as Mahavir, Mahaveer, and Mahavīr. With diacritical marks, we can establish a definitive spelling.|We should include the Devanagari and Gujarati versions of each word. Once crowdsourcing is enabled, we can ask the public to submit Gujarati and Hindi explanations of the terms that do not necessarily follow the English explanation.",
    "index.visionBody":
      "Jainlexicon.org is a multimedia platform designed to explain Jain terminologies with proper pronunciation guide and pictures etc.\n\nThis is a collaborative project that will always be in a constant state of \"Work in progress\". This site is a learning tool for studying Jainism. You are not only invited to browse but also to contribute. Any user can suggest changes or add new terms to make the site more useful to students and scholars of Jainism.\n\nLet's concentrate on preparing the English version first. It is critical to define definitive English spellings of Jain words. Presently, we see words such as Mahavir, Mahaveer, and Mahavīr. With diacritical marks, we can establish a definitive spelling.\n\nWe should include the Devanagari and Gujarati versions of each word. Once crowdsourcing is enabled, we can ask the public to submit Gujarati and Hindi explanations of the terms that do not necessarily follow the English explanation.",
    "index.statTerms": "Terms",
    "index.statLanguages": "Languages",
    "index.statLanguagesValue": "EN · HI · GU",
    "index.statCommunity": "Community Driven",
    "index.statCommunityValue": "Open",
    "index.ctaTitle": "Contribute to Sacred Knowledge",
    "index.ctaBody":
      "Help us build the most comprehensive Jain terminology resource.",
    "index.startContributing": "Start Contributing",
    "index.browseTermsButton": "Browse Terms",
    "footer.blurb":
      "A multimedia platform designed to explain Jain terminologies with proper pronunciation guide and meanings in Hindi and Gujarati.",
    "footer.quickLinks": "Quick Links",
    "footer.about": "About",
    "footer.linkBrowseTerms": "Browse Terms",
    "footer.advisorLabel": "Academic Advisor:",
    "footer.editorLabel": "General Editor:",
    "footer.copyright":
      "© {{year}} Jain Lexicon. All rights reserved.",
    "footer.orgLogoAlt": "Shrut Ratnakar",
    "footer.developedBy": "Developed by",
    "footer.universalSoftware": "Universal Software",
    "footer.preserving": "Preserving sacred knowledge for future generations",
    "stats.terms": "Terms",
    "stats.languages": "Languages",
    "stats.contributors": "Contributors",
    "contrib.loadFailed": "Could not load your contributions.",
    "contrib.signInTitle": "Sign in to see your submissions",
    "contrib.signInBody":
      "After you log in, your pending and reviewed terms appear here.",
    "contrib.signInButton": "Log in",
    "contrib.statTotal": "Total",
    "contrib.statPending": "Pending",
    "contrib.statApproved": "Approved",
    "contrib.statRejected": "Rejected",
    "contrib.colIndex": "#",
    "contrib.colWord": "Word",
    "contrib.colMeaningEn": "Meaning (English)",
    "contrib.colStatus": "Status",
    "contrib.colSubmitted": "Submitted",
    "contrib.colEmail": "Email",
    "contrib.noContributions": "No contributions yet",
    "contrib.noContributionsHint":
      "Use the submit button above to send your first term.",
    "contrib.statusPending": "Pending",
    "contrib.statusApproved": "Approved",
    "contrib.statusRejected": "Rejected",
    "form.loginRequired": "Login Required",
    "form.loginRequiredBody":
      "Please login or create an account to contribute terms to the lexicon.",
    "form.loginRegisterButton": "Login / Register",
    "form.accountDeactivated": "Account Deactivated",
    "form.accountDeactivatedBody":
      "Your account has been deactivated by the administrator. Please contact admin for assistance.",
    "form.sessionExpired": "Your session expired. Please sign in again.",
    "form.profileLoadFailed":
      "Your profile could not be loaded. Please refresh the page and try again.",
    "form.submitFailed": "Failed to submit contribution. Please try again.",
    "form.existingTerms": "Existing terms",
    "form.viewTerm": "View →",
    "form.termAlreadyExists":
      "\"{{word}}\" already exists in the lexicon.",
    "form.viewExistingTerm": "View existing term →",
    "form.submitting": "Submitting...",
    "form.validate.wordRequired": "Word is required",
    "form.validate.nameRequired": "Name is required",
    "form.validate.emailRequired": "Email is required",
    "form.validate.emailInvalid": "Enter a valid email",
    "form.validate.oneMeaning":
      "At least one meaning (English, Hindi, or Gujarati) is required",
    "form.audioLabel": "Audio Attachment",
    "form.audioInvalidType": "Please select an audio file",
    "form.audioPickDifferent": "Pick different file",
    "form.audioAttachReplace": "Attach / replace",
    "form.audioRemoveNew": "Remove new upload",
    "form.audioSaving": "Saving…",
    "form.audioAttach": "Attach audio file",
  },
  hi: {
    "site.title": "जैन शब्दकोश",
    "site.subtitle": "जैन शब्दावली का एक व्यापक शब्दकोश",
    "nav.home": "होम",
    "nav.browse": "ब्राउज़ करें",
    "nav.contribute": "योगदान करें",
    "nav.about": "परिचय",
    "nav.search": "खोज",
    "header.login": "लॉग इन",
    "header.language": "भाषा",
    "header.myAccount": "मेरा खाता",
    "header.accountPassword": "खाता और पासवर्ड",
    "header.logout": "लॉग आउट",
    "header.admin": "प्रशासन",
    "header.adminTooltip": "डैशबोर्ड",
    "header.adminPortal": "प्रशासन पोर्टल",
    "header.menuMyAccount": "मेरा खाता",
    "header.resetPassword": "पासवर्ड रीसेट",
    "header.openMenu": "मेनू खोलें",
    "header.closeMenu": "मेनू बंद करें",
    "header.myContributions": "मेरे योगदान",
    "header.mainNav": "मुख्य नेविगेशन",
    "search.placeholder": "शब्द खोजें...",
    "search.button": "खोजें",
    "search.title": "खोज परिणाम",
    "search.resultsFor": "\"{{query}}\" के लिए परिणाम",
    "search.backToBrowse": "ब्राउज़ पर वापस",
    "search.foundSingular": "{{count}} परिणाम मिला",
    "search.foundPlural": "{{count}} परिणाम मिले",
    "search.loadErrorTitle": "परिणाम लोड नहीं हो सके",
    "search.loadErrorBody":
      "डेटाबेस से जुड़ने में समस्या आई। कृपया पुनः प्रयास करें।",
    "search.noResults": "कोई परिणाम नहीं",
    "search.noResultsHint":
      "अन्य कीवर्ड से खोजें या वर्णानुक्रम से ब्राउज़ करें",
    "search.browseAll": "सभी शब्द ब्राउज़ करें",
    "alphabetical.title": "वर्णानुक्रम से ब्राउज़ करें",
    "browse.subtitle":
      "जैन शब्दावली का पूरा कोश जो वर्णानुक्रम में व्यवस्थित है",
    "browse.filteringByLetter": "अक्षर से फ़िल्टर",
    "browse.termsStartingWith": "\"{{letter}}\" से शुरू होने वाले शब्द",
    "browse.noTermsLetter":
      "\"{{letter}}\" से शुरू होने वाला कोई शब्द नहीं मिला",
    "browse.noTermsGeneric": "कोई शब्द नहीं मिला",
    "browse.tryDifferentLetter":
      "दूसरा अक्षर चुनें या पहला योगदान देने वाले बनें!",
    "browse.emptyGeneric": "जैन शब्दकोश में पहला योगदान दें!",
    "browse.perPage": "प्रति पृष्ठ",
    "browse.paginationPrev": "पिछला",
    "browse.paginationNext": "अगला",
    "browse.paginationPageOf": "पृष्ठ {{current}} / {{total}}",
    "browse.paginationRange": "{{start}}–{{end}}, कुल {{total}}",
    "term.meaning": "अर्थ",
    "term.pronunciation": "उच्चारण",
    "term.hindi": "हिंदी",
    "term.gujarati": "गुजराती",
    "term.english": "अंग्रेज़ी",
    "termCard.viewTerm": "शब्द देखें",
    "termCard.viewOneLanguage": "{{count}} भाषा देखें",
    "termCard.viewAllLanguages": "सभी {{count}} भाषाएँ देखें",
    "termCard.exactMatch": "सटीक मिलान",
    "termDetail.backToBrowse": "ब्राउज़ पर वापस",
    "termDetail.notFound": "शब्द नहीं मिला",
    "termDetail.notFoundBody":
      "आप जो शब्द ढूँढ रहे हैं वह मौजूद नहीं है या हटा दिया गया है।",
    "termDetail.browseAllTerms": "सभी शब्द ब्राउज़ करें",
    "termDetail.exampleUsage": "उदाहरण",
    "termDetail.quickInfo": "संक्षिप्त जानकारी",
    "termDetail.firstLetter": "पहला अक्षर",
    "termDetail.languagesLabel": "भाषाएँ",
    "termDetail.relatedTerms": "संबंधित शब्द",
    "termDetail.recordedPronunciation": "ऑडियो उच्चारण",
    "termDetail.audioPlay": "उच्चारण चलाएँ",
    "termDetail.audioPause": "रोकें",
    "termDetail.audioMute": "आवाज़ बंद करें",
    "termDetail.audioUnmute": "आवाज़ चालू करें",
    "termDetail.linkCopied": "लिंक क्लिपबोर्ड पर कॉपी हो गया!",
    "termDetail.shareText": "{{word}} के बारे में जानें - जैन शब्दकोश",
    "wotd.featured": "विशेष",
    "wotd.title": "आज का शब्द",
    "wotd.exploreFull": "पूरा विवरण देखें",
    "contribute.title": "नया शब्द जोड़ें",
    "contribute.description":
      "नए जैन शब्दों को प्रस्तुत करके हमारे शब्दकोश का विस्तार करने में मदद करें",
    "contribute.word": "शब्द",
    "contribute.meaningHindi": "हिंदी में अर्थ",
    "contribute.meaningGujarati": "गुजराती में अर्थ",
    "contribute.pronunciation": "उच्चारण (वैकल्पिक)",
    "contribute.example": "उदाहरण (वैकल्पिक)",
    "contribute.email": "आपका ईमेल",
    "contribute.contributorEmail": "योगदानकर्ता ईमेल",
    "contribute.yourInfo": "आपकी जानकारी *",
    "contribute.identityFromProfile": "आपके खाते की प्रोफ़ाइल से लिया गया।",
    "contribute.name": "आपका नाम",
    "contribute.submit": "योगदान जमा करें",
    "contribute.success":
      "धन्यवाद! आपका योगदान समीक्षा के लिए जमा कर दिया गया है।",
    "contribute.guidelinesBadge": "योगदान दिशानिर्देश",
    "contribute.g1Title": "प्रामाणिक शब्द",
    "contribute.g1Desc":
      "सुनिश्चित करें कि शब्द जैन दर्शन, आचरण या ग्रंथों से संबंधित हो।",
    "contribute.g2Title": "सटीक अर्थ",
    "contribute.g2Desc":
      "कम से कम एक भाषा (हिंदी या गुजराती) में सटीक अर्थ दें।",
    "contribute.g3Title": "समीक्षा प्रक्रिया",
    "contribute.g3Desc":
      "सभी प्रस्ताव प्रकाशन से पहले विशेषज्ञों द्वारा समीक्षित होते हैं।",
    "contribute.g4Title": "गुणवत्ता मानक",
    "contribute.g4Desc":
      "जहाँ संभव हो उच्चारण मार्गदर्शन शामिल करें।",
    "contribute.myContributions": "मेरे योगदान",
    "contribute.trackStatus": "आपके जमा किए शब्दों की स्थिति देखें।",
    "contribute.showingFor": "इसके लिए प्रस्ताव दिखा रहे हैं",
    "contribute.submitNew": "नया शब्द जमा करें",
    "contribute.submittingAs": "इस पहचान से जमा:",
    "contribute.successReviewBody":
      "प्रकाशन से पहले हमारी टीम द्वारा आपके शब्द की समीक्षा की जाएगी।",
    "contribute.submitAnother": "और एक शब्द जमा करें",
    "contribute.viewMyContributions": "मेरे योगदान देखें",
    "contribute.wordPlaceholder": "उदा., अहिंसा, अनेकांतवाद",
    "contribute.meaningEnglishPlaceholder": "अंग्रेज़ी में अर्थ लिखें...",
    "contribute.pronunciationPlaceholder": "उदा., suhm-yuk",
    "contribute.examplePlaceholder":
      "संदर्भ में शब्द का प्रयोग कैसे होता है...",
    "hero.title": "पवित्र भाषा का",
    "hero.titleAccent": "अन्वेषण करें",
    "hero.description":
      "हिंदी और गुजराती में जैन शब्दावली के गहन अर्थों की खोज करें",
    "index.heroTagline": "पवित्र ज्ञान • कालातीत बुद्धिमत्ता",
    "quickLinks.searchTitle": "शब्द खोजें",
    "quickLinks.searchDesc":
      "शक्तिशाली खोज से किसी भी जैन शब्द को तुरंत खोजें",
    "quickLinks.browseTitle": "शब्दकोश ब्राउज़ करें",
    "quickLinks.browseDesc": "सभी शब्द वर्णानुक्रम में देखें",
    "quickLinks.contributeDesc":
      "नए शब्द जोड़कर शब्दकोश का विस्तार करने में मदद करें",
    "quickLinks.exploreTitle": "अन्वेषण",
    "quickLinks.exploreDesc": "पवित्र ज्ञान और बुद्धिमत्ता खोजें",
    "index.creditsAdvisor": "शैक्षिक सलाहकार",
    "index.creditsEditor": "मुख्य संपादक",
    "index.addTerm": "शब्द जोड़ें",
    "index.quickAccess": "त्वरित पहुँच",
    "index.featuredTerms": "विशेष शब्द",
    "index.featuredSubtitle": "जैन दर्शन का ज्ञान अन्वेषित करें",
    "index.viewAllTerms": "सभी शब्द देखें",
    "index.showMoreFeatured": "और देखें",
    "index.showLessFeatured": "कम देखें",
    "common.loadErrorTitle": "शब्द लोड नहीं हो सके",
    "common.loadErrorBody":
      "सेवा से शब्द लोड करने में समस्या आई। कृपया पुनः प्रयास करें।",
    "common.retry": "पुनः प्रयास",
    "index.noTermsTitle": "अभी कोई शब्द नहीं",
    "index.noTermsBody": "जैन शब्दकोश में पहला योगदान दें!",
    "common.addTerm": "शब्द जोड़ें",
    "index.purposeEyebrow": "जैन शब्दकोश",
    "index.purposeTitle": "हमारा उद्देश्य",
    "index.purposeSubtitle":
      "जैन शब्दावली, उच्चारण और बहुभाषी अध्ययन के लिए आधुनिक, विरासत-प्रेरित मंच।",
    "index.mission": "हमारा मिशन",
    "index.missionLead":
      "जैन शब्दों को स्पष्टता, श्रद्धा और वास्तविक अध्ययन के उपकरणों के साथ समझाना।",
    "index.missionBullets":
      "उच्चारण मार्गदर्शन, चित्र और संदर्भ के साथ मल्टीमीडिया प्रविष्टियाँ|मानकीकृत द्यानुक्त चिह्नों के साथ अंग्रेज़ी अर्थ|प्रत्येक शब्द के साथ हिंदी और गुजराती व्याख्याएँ|विद्यार्थियों, विद्वानों और जैन समुदाय के लिए",
    "index.missionBody":
      "जैन शब्दकोश एक मल्टीमीडिया मंच है जो जैन शब्दों को उच्चारण मार्गदर्शन और हिंदी तथा गुजराती में अर्थों के साथ समझाता है।",
    "index.vision": "हमारा विजन",
    "index.visionLead": "",
    "index.visionBullets":
      "Jainlexicon.org एक मल्टीमीडिया मंच है जो जैन शब्दावली को उचित उच्चारण मार्गदर्शन, चित्र आदि के साथ समझाने के लिए डिज़ाइन किया गया है।|यह एक सहयोगी परियोजना है जो हमेशा \"प्रगति पर कार्य\" की स्थिति में रहेगी। यह साइट जैन धर्म का अध्ययन करने के लिए एक शिक्षण उपकरण है।|आप केवल ब्राउज़ करने के लिए ही नहीं, बल्कि योगदान करने के लिए भी आमंत्रित हैं। कोई भी उपयोगकर्ता परिवर्तन सुझा सकता है या नए शब्द जोड़ सकता है ताकि यह साइट जैन धर्म के विद्यार्थियों और विद्वानों के लिए अधिक उपयोगी हो सके।|पहले अंग्रेज़ी संस्करण तैयार करने पर ध्यान केंद्रित करें। जैन शब्दों की निश्चित अंग्रेज़ी वर्तनी परिभाषित करना महत्वपूर्ण है। वर्तमान में हम Mahavir, Mahaveer और Mahavīr जैसे शब्द देखते हैं। द्यानुक्त चिह्नों के साथ हम एक निश्चित वर्तनी स्थापित कर सकते हैं।|हमें प्रत्येक शब्द के देवनागरी और गुजराती संस्करण शामिल करने चाहिए। क्राउडसोर्सिंग सक्षम होने पर, हम जनता से उन शब्दों की गुजराती और हिंदी व्याख्याएँ जमा करने का अनुरोध कर सकते हैं जो अंग्रेज़ी व्याख्या का अनिवार्य अनुसरण नहीं करतीं।",
    "index.visionBody":
      "Jainlexicon.org एक मल्टीमीडिया मंच है जो जैन शब्दावली को उचित उच्चारण मार्गदर्शन, चित्र आदि के साथ समझाने के लिए डिज़ाइन किया गया है।\n\nयह एक सहयोगी परियोजना है जो हमेशा \"प्रगति पर कार्य\" की स्थिति में रहेगी। यह साइट जैन धर्म का अध्ययन करने के लिए एक शिक्षण उपकरण है। आप केवल ब्राउज़ करने के लिए ही नहीं, बल्कि योगदान करने के लिए भी आमंत्रित हैं। कोई भी उपयोगकर्ता परिवर्तन सुझा सकता है या नए शब्द जोड़ सकता है ताकि यह साइट जैन धर्म के विद्यार्थियों और विद्वानों के लिए अधिक उपयोगी हो सके।\n\nपहले अंग्रेज़ी संस्करण तैयार करने पर ध्यान केंद्रित करें। जैन शब्दों की निश्चित अंग्रेज़ी वर्तनी परिभाषित करना महत्वपूर्ण है। वर्तमान में हम Mahavir, Mahaveer और Mahavīr जैसे शब्द देखते हैं। द्यानुक्त चिह्नों के साथ हम एक निश्चित वर्तनी स्थापित कर सकते हैं।\n\nहमें प्रत्येक शब्द के देवनागरी और गुजराती संस्करण शामिल करने चाहिए। क्राउडसोर्सिंग सक्षम होने पर, हम जनता से उन शब्दों की गुजराती और हिंदी व्याख्याएँ जमा करने का अनुरोध कर सकते हैं जो अंग्रेज़ी व्याख्या का अनिवार्य अनुसरण नहीं करतीं।",
    "index.statTerms": "शब्द",
    "index.statLanguages": "भाषाएँ",
    "index.statLanguagesValue": "EN · HI · GU",
    "index.statCommunity": "समुदाय आधारित",
    "index.statCommunityValue": "खुला",
    "index.ctaTitle": "पवित्र ज्ञान में योगदान दें",
    "index.ctaBody":
      "सर्वाधिक व्यापक जैन शब्दावली संसाधन बनाने में हमारी मदद करें।",
    "index.startContributing": "योगदान शुरू करें",
    "index.browseTermsButton": "शब्द ब्राउज़ करें",
    "footer.blurb":
      "जैन शब्दों को उच्चारण मार्गदर्शन और हिंदी तथा गुजराती में अर्थों के साथ समझाने वाला मल्टीमीडिया मंच।",
    "footer.quickLinks": "त्वरित लिंक",
    "footer.about": "परिचय",
    "footer.linkBrowseTerms": "शब्द ब्राउज़ करें",
    "footer.advisorLabel": "शैक्षिक सलाहकार:",
    "footer.editorLabel": "मुख्य संपादक:",
    "footer.copyright": "© {{year}} जैन शब्दकोश। सर्वाधिकार सुरक्षित।",
    "footer.orgLogoAlt": "श्रुत रत्नाकर",
    "footer.developedBy": "विकसित:",
    "footer.universalSoftware": "Universal Software",
    "footer.preserving": "आने वाली पीढ़ियों के लिए पवित्र ज्ञान का संरक्षण",
    "stats.terms": "शब्द",
    "stats.languages": "भाषाएं",
    "stats.contributors": "योगदानकर्ता",
    "contrib.loadFailed": "आपके योगदान लोड नहीं हो सके।",
    "contrib.signInTitle": "अपने प्रस्ताव देखने के लिए साइन इन करें",
    "contrib.signInBody":
      "लॉग इन के बाद, आपके लंबित और समीक्षित शब्द यहाँ दिखेंगे।",
    "contrib.signInButton": "लॉग इन",
    "contrib.statTotal": "कुल",
    "contrib.statPending": "लंबित",
    "contrib.statApproved": "स्वीकृत",
    "contrib.statRejected": "अस्वीकृत",
    "contrib.colIndex": "क्र.",
    "contrib.colWord": "शब्द",
    "contrib.colMeaningEn": "अर्थ (अंग्रेज़ी)",
    "contrib.colStatus": "स्थिति",
    "contrib.colSubmitted": "जमा किया",
    "contrib.colEmail": "ईमेल",
    "contrib.noContributions": "अभी कोई योगदान नहीं",
    "contrib.noContributionsHint":
      "अपना पहला शब्द भेजने के लिए ऊपर सबमिट बटन का उपयोग करें।",
    "contrib.statusPending": "लंबित",
    "contrib.statusApproved": "स्वीकृत",
    "contrib.statusRejected": "अस्वीकृत",
    "form.loginRequired": "लॉग इन आवश्यक",
    "form.loginRequiredBody":
      "शब्दकोश में योगदान देने के लिए कृपया लॉग इन करें या खाता बनाएं।",
    "form.loginRegisterButton": "लॉग इन / पंजीकरण",
    "form.accountDeactivated": "खाता निष्क्रिय",
    "form.accountDeactivatedBody":
      "आपका खाता प्रशासक द्वारा निष्क्रिय कर दिया गया है। सहायता के लिए संपर्क करें।",
    "form.sessionExpired": "आपका सत्र समाप्त हो गया। कृपया पुनः साइन इन करें।",
    "form.profileLoadFailed":
      "प्रोफ़ाइल लोड नहीं हो सकी। पृष्ठ रीफ़्रेश करके पुनः प्रयास करें।",
    "form.submitFailed": "योगदान जमा नहीं हो सका। पुनः प्रयास करें।",
    "form.existingTerms": "मौजूदा शब्द",
    "form.viewTerm": "देखें →",
    "form.termAlreadyExists":
      "\"{{word}}\" शब्दकोश में पहले से मौजूद है।",
    "form.viewExistingTerm": "मौजूदा शब्द देखें →",
    "form.submitting": "जमा हो रहा है...",
    "form.validate.wordRequired": "शब्द आवश्यक है",
    "form.validate.nameRequired": "नाम आवश्यक है",
    "form.validate.emailRequired": "ईमेल आवश्यक है",
    "form.validate.emailInvalid": "वैध ईमेल दर्ज करें",
    "form.validate.oneMeaning":
      "कम से कम एक अर्थ (अंग्रेज़ी, हिंदी या गुजराती) आवश्यक है",
    "form.audioLabel": "ऑडियो संलग्नक",
    "form.audioInvalidType": "कृपया ऑडियो फ़ाइल चुनें",
    "form.audioPickDifferent": "दूसरी फ़ाइल चुनें",
    "form.audioAttachReplace": "जोड़ें / बदलें",
    "form.audioRemoveNew": "नया अपलोड हटाएँ",
    "form.audioSaving": "सहेजा जा रहा है…",
    "form.audioAttach": "ऑडियो फ़ाइल जोड़ें",
  },
  gu: {
    "site.title": "જૈન શબ્દકોશ",
    "site.subtitle": "જૈન શબ્દાવલીનો વ્યાપક શબ્દકોશ",
    "nav.home": "હોમ",
    "nav.browse": "બ્રાઉઝ કરો",
    "nav.contribute": "યોગદાન આપો",
    "nav.about": "વિશે",
    "nav.search": "શોધ",
    "header.login": "લૉગ ઇન",
    "header.language": "ભાષા",
    "header.myAccount": "મારું એકાઉન્ટ",
    "header.accountPassword": "એકાઉન્ટ અને પાસવર્ડ",
    "header.logout": "લૉગ આઉટ",
    "header.admin": "પ્રશાસન",
    "header.adminTooltip": "ડેશબોર્ડ",
    "header.adminPortal": "પ્રશાસન પોર્ટલ",
    "header.menuMyAccount": "મારું એકાઉન્ટ",
    "header.resetPassword": "પાસવર્ડ રીસેટ",
    "header.openMenu": "મેનૂ ખોલો",
    "header.closeMenu": "મેનું બંધ કરો",
    "header.myContributions": "મારું યોગદાન",
    "header.mainNav": "મુખ્ય નેવિગેશન",
    "search.placeholder": "શબ્દ શોધો...",
    "search.button": "શોધો",
    "search.title": "શોધ પરિણામો",
    "search.resultsFor": "\"{{query}}\" માટે પરિણામો",
    "search.backToBrowse": "બ્રાઉઝ પર પાછા",
    "search.foundSingular": "{{count}} પરિણામ મળ્યું",
    "search.foundPlural": "{{count}} પરિણામો મળ્યાં",
    "search.loadErrorTitle": "પરિણામો લોડ થયા નહીં",
    "search.loadErrorBody":
      "ડેટાબેઝ સાથે જોડાવામાં સમસ્યા આવી। કૃપા કરીને ફરી પ્રયાસ કરો.",
    "search.noResults": "કોઈ પરિણામ નથી",
    "search.noResultsHint":
      "વિવિધ શબ્દોથી શોધો અથવા મૂળાક્ષર પ્રમાણે બ્રાઉઝ કરો",
    "search.browseAll": "તમામ શબ્દો બ્રાઉઝ કરો",
    "alphabetical.title": "મૂળાક્ષર પ્રમાણે બ્રાઉઝ કરો",
    "browse.subtitle":
      "મૂળાક્ષર પ્રમાણે ગોઠવેલ જૈન શબ્દાવલીનો સંપૂર્ણ શબ્દકોશ શોધો",
    "browse.filteringByLetter": "અક્ષરથી ફિલ્ટર",
    "browse.termsStartingWith": "\"{{letter}}\" થી શરૂ થતા શબ્દો",
    "browse.noTermsLetter":
      "\"{{letter}}\" થી શરૂ થતા કોઈ શબ્દ મળ્યા નથી",
    "browse.noTermsGeneric": "કોઈ શબ્દ મળ્યો નહીં",
    "browse.tryDifferentLetter":
      "અન્ય અક્ષર પસંદ કરો અથવા પ્રથમ યોગદાન આપનાર બનો!",
    "browse.emptyGeneric": "જૈન શબ્દકોશમાં પ્રથમ યોગદાન આપો!",
    "browse.perPage": "પ્રતિ પૃષ્ઠ",
    "browse.paginationPrev": "પાછળ",
    "browse.paginationNext": "આગળ",
    "browse.paginationPageOf": "પૃષ્ઠ {{current}} / {{total}}",
    "browse.paginationRange": "{{start}}–{{end}}, કુલ {{total}}",
    "term.meaning": "અર્થ",
    "term.pronunciation": "ઉચ્ચારણ",
    "term.hindi": "હિન્દી",
    "term.gujarati": "ગુજરાતી",
    "term.english": "અંગ્રેજી",
    "termCard.viewTerm": "શબ્દ જુઓ",
    "termCard.viewOneLanguage": "{{count}} ભાષા જુઓ",
    "termCard.viewAllLanguages": "બધી {{count}} ભાષાઓ જુઓ",
    "termCard.exactMatch": "ચોક્કસ મેળ",
    "termDetail.backToBrowse": "બ્રાઉઝ પર પાછા",
    "termDetail.notFound": "શબ્દ મળ્યો નહીં",
    "termDetail.notFoundBody":
      "તમે જે શબ્દ શોધો છો તે અસ્તિત્વમાં નથી અથવા કાઢી નાખ્યો છે.",
    "termDetail.browseAllTerms": "તમામ શબ્દો બ્રાઉઝ કરો",
    "termDetail.exampleUsage": "ઉદાહરણ",
    "termDetail.quickInfo": "સંક્ષિપ્ત માહિતી",
    "termDetail.firstLetter": "પ્રથમ અક્ષર",
    "termDetail.languagesLabel": "ભાષાઓ",
    "termDetail.relatedTerms": "સંબંધિત શબ્દો",
    "termDetail.recordedPronunciation": "ઑડિયો ઉચ્ચારણ",
    "termDetail.audioPlay": "ઉચ્ચારણ ચલાવો",
    "termDetail.audioPause": "અટકાવો",
    "termDetail.audioMute": "અવાજ બંધ કરો",
    "termDetail.audioUnmute": "અવાજ ચાલુ કરો",
    "termDetail.linkCopied": "લિંક ક્લિપબોર્ડ પર કૉપી થઈ!",
    "termDetail.shareText": "{{word}} વિશે જાણો - જૈન શબ્દકોશ",
    "wotd.featured": "વિશેષ",
    "wotd.title": "આજનો શબ્દ",
    "wotd.exploreFull": "પૂર્ણ વ્યાખ્યા જુઓ",
    "contribute.title": "નવો શબ્દ ઉમેરો",
    "contribute.description":
      "નવા જૈન શબ્દો સબમિટ કરીને અમારા શબ્દકોશને વિસ્તૃત કરવામાં મદદ કરો",
    "contribute.word": "શબ્દ",
    "contribute.meaningHindi": "હિન્દીમાં અર્થ",
    "contribute.meaningGujarati": "ગુજરાતીમાં અર્થ",
    "contribute.pronunciation": "ઉચ્ચારણ (વૈકલ્પિક)",
    "contribute.example": "ઉદાહરણ (વૈકલ્પિક)",
    "contribute.email": "તમારું ઇમેઇલ",
    "contribute.contributorEmail": "યોગદાનકર્તાનું ઇમેઇલ",
    "contribute.yourInfo": "તમારી માહિતી *",
    "contribute.identityFromProfile": "તમારા એકાઉન્ટ પ્રોફાઇલમાંથી લીધેલ છે.",
    "contribute.name": "તમારું નામ",
    "contribute.submit": "યોગદાન સબમિટ કરો",
    "contribute.success":
      "આભાર! તમારું યોગદાન સમીક્ષા માટે સબમિટ કરવામાં આવ્યું છે.",
    "contribute.guidelinesBadge": "યોગદાન માર્ગદર્શિકા",
    "contribute.g1Title": "પ્રામાણિક શબ્દો",
    "contribute.g1Desc":
      "ખાતરી કરો કે શબ્દ જૈન દર્શન, આચરણ અથવા ગ્રંથોથી સંબંધિત છે.",
    "contribute.g2Title": "સચોટ અર્થ",
    "contribute.g2Desc":
      "ઓછામાં ઓછી એક ભાષા (હિન્દી અથવા ગુજરાતી)માં સચોટ અર્થ આપો.",
    "contribute.g3Title": "સમીક્ષા પ્રક્રિયા",
    "contribute.g3Desc":
      "બધી રજૂઆતો પ્રકાશન પહેલાં નિષ્ણાતો દ્વારા તપાસાય છે.",
    "contribute.g4Title": "ગુણવત્તા ધોરણો",
    "contribute.g4Desc":
      "શક્ય હોય ત્યારે ઉચ્ચારણ માર્ગદર્શન ઉમેરો.",
    "contribute.myContributions": "મારું યોગદાન",
    "contribute.trackStatus": "તમે સબમિટ કરેલ શબ્દોની સ્થિતિ ટ્રેક કરો.",
    "contribute.showingFor": "આ માટે રજૂઆતો દર્શાવી રહ્યા છે",
    "contribute.submitNew": "નવો શબ્દ સબમિટ કરો",
    "contribute.submittingAs": "આ પ્રમાણે સબમિટ:",
    "contribute.successReviewBody":
      "પ્રકાશિત કરતા પહેલાં અમારી ટીમ તમારા શબ્દની સમીક્ષા કરશે.",
    "contribute.submitAnother": "બીજો શબ્દ સબમિટ કરો",
    "contribute.viewMyContributions": "મારું યોગદાન જુઓ",
    "contribute.wordPlaceholder": "ઉદા., અહિંસા, અનેકાંતવાદ",
    "contribute.meaningEnglishPlaceholder": "અંગ્રેજીમાં અર્થ લખો...",
    "contribute.pronunciationPlaceholder": "ઉદા., suhm-yuk",
    "contribute.examplePlaceholder":
      "સંદર્ભમાં શબ્દ કેવી રીતે વપરાય છે તે લખો...",
    "hero.title": "પવિત્ર ભાષાનું",
    "hero.titleAccent": "અન્વેષણ કરો",
    "hero.description": "હિન્દી અને ગુજરાતીમાં જૈન શબ્દાવલીના ગહન અર્થો શોધો",
    "index.heroTagline": "પવિત્ર જ્ઞાન • કાલાતીત શાણપ",
    "quickLinks.searchTitle": "શબ્દો શોધો",
    "quickLinks.searchDesc":
      "શોધ દ્વારા કોઈ પણ જૈન શબ્દ તરત શોધો",
    "quickLinks.browseTitle": "શબ્દકોશ બ્રાઉઝ કરો",
    "quickLinks.browseDesc":
      "મૂળાક્ષર પ્રમાણે ગોઠવેલ તમામ શબ્દો જુઓ",
    "quickLinks.contributeDesc":
      "નવા શબ્દો ઉમેરીને શબ્દકોશ વિસ્તૃત કરવામાં મદદ કરો",
    "quickLinks.exploreTitle": "શોધો",
    "quickLinks.exploreDesc": "પવિત્ર જ્ઞાન શોધો",
    "index.creditsAdvisor": "શૈક્ષણિક સલાહકાર",
    "index.creditsEditor": "મુખ્ય સંપાદક",
    "index.addTerm": "શબ્દ ઉમેરો",
    "index.quickAccess": "ઝડપી પ્રવેશ",
    "index.featuredTerms": "વિશેષ શબ્દો",
    "index.featuredSubtitle": "જૈન દર્શનનું જ્ઞાન શોધો",
    "index.viewAllTerms": "બધા શબ્દો જુઓ",
    "index.showMoreFeatured": "વધુ જુઓ",
    "index.showLessFeatured": "ઓછું જુઓ",
    "common.loadErrorTitle": "શબ્દો લોડ થયા નહીં",
    "common.loadErrorBody":
      "સેવામાંથી શબ્દો લોડ કરવામાં સમસ્યા આવી। કૃપા કરીને ફરી પ્રયાસ કરો.",
    "common.retry": "ફરી પ્રયાસ",
    "index.noTermsTitle": "હજુ કોઈ શબ્દ નથી",
    "index.noTermsBody": "જૈન શબ્દકોશમાં પ્રથમ યોગદાન આપો!",
    "common.addTerm": "શબ્દ ઉમેરો",
    "index.purposeEyebrow": "જૈન શબ્દકોશ",
    "index.purposeTitle": "અમારો હેતુ",
    "index.purposeSubtitle":
      "જૈન શબ્દાવલી, ઉચ્ચારણ અને બહુભાષી અભ્યાસ માટે આધુનિક, વારસો-પ્રેરિત પ્લેટફોર્મ.",
    "index.mission": "અમારું લક્ષ્ય",
    "index.missionLead":
      "જૈન શબ્દો સ્પષ્ટતા, શ્રદ્ધા અને વાસ્તવિક અભ્યાસના સાધનો સાથે સમજાવવા.",
    "index.missionBullets":
      "ઉચ્ચારણ માર્ગદર્શન, ચિત્રો અને સંદર્ભ સાથે મલ્ટિમીડિયા પ્રવેશો|માનકીકૃત દ્યાનુક્ત ચિહ્નો સાથે અંગ્રેજી અર્થ|દરેક શબ્દ સાથે હિન્દી અને ગુજરાતી સમજાવટ|વિદ્યાર્થીઓ, વિદ્વાનો અને જૈન સમુદાય માટે",
    "index.missionBody":
      "જૈન શબ્દકોશ મલ્ટિમીડિયા પ્લેટફોર્મ છે જે જૈન શબ્દો હિન્દી અને ગુજરાતીમાં અર્થ તથા ઉચ્ચારણ માર્ગદર્શન સાથે સમજાવે છે.",
    "index.vision": "અમારું દ્રષ્ટિકોન",
    "index.visionLead": "",
    "index.visionBullets":
      "Jainlexicon.org એ મલ્ટિમીડિયા પ્લેટફોર્મ છે જે જૈન શબ્દાવલીને યોગ્ય ઉચ્ચારણ માર્ગદર્શન, ચિત્રો વગેરે સાથે સમજાવવા માટે ડિઝાઇન કરવામાં આવ્યું છે.|આ સહયોગી પ્રકલ્પ હંમેશા \"પ્રગતિમાં કાર્ય\"ની સ્થિતિમાં રહેશે. આ સાઇટ જૈન ધર્મનો અભ્યાસ કરવા માટે શિક્ષણ સાધન છે.|તમે માત્ર બ્રાઉઝ કરવા માટે જ નહીં, પણ યોગદાન આપવા માટે પણ આમંત્રિત છો. કોઈપણ વપરાશકર્તા ફેરફાર સૂચવી શકે છે અથવા નવા શબ્દો ઉમેરી શકે છે જેથી આ સાઇટ જૈન ધર્મના વિદ્યાર્થીઓ અને વિદ્વાનો માટે વધુ ઉપયોગી બને.|પહેલા અંગ્રેજી સંસ્કરણ તૈયાર કરવા પર ધ્યાન કેન્દ્રિત કરીએ. જૈન શબ્દોની નિશ્ચિત અંગ્રેજી જોડણી વ્યાખ્યાયિત કરવી મહત્વપૂર્ણ છે. હાલમાં આપણે Mahavir, Mahaveer અને Mahavīr જેવા શબ્દો જોઈએ છીએ. દ્યાનુક્ત ચિહ્નો સાથે આપણે નિશ્ચિત જોડણી સ્થાપિત કરી શકીએ.|આપણે દરેક શબ્દના દેવનાગરી અને ગુજરાતી સંસ્કરણો શામેલ કરવા જોઈએ. ક્રાઉડસોર્સિંગ સક્ષમ થયા પછી, આપણે જનતાને તે શબ્દોની ગુજરાતી અને હિન્દી સમજાવટો સબમિટ કરવા કહી શકીએ જે અંગ્રેજી સમજાવટનું અનિવાર્ય અનુસરણ નથી કરતી.",
    "index.visionBody":
      "Jainlexicon.org એ મલ્ટિમીડિયા પ્લેટફોર્મ છે જે જૈન શબ્દાવલીને યોગ્ય ઉચ્ચારણ માર્ગદર્શન, ચિત્રો વગેરે સાથે સમજાવવા માટે ડિઝાઇન કરવામાં આવ્યું છે.\n\nઆ સહયોગી પ્રકલ્પ હંમેશા \"પ્રગતિમાં કાર્ય\"ની સ્થિતિમાં રહેશે. આ સાઇટ જૈન ધર્મનો અભ્યાસ કરવા માટે શિક્ષણ સાધન છે. તમે માત્ર બ્રાઉઝ કરવા માટે જ નહીં, પણ યોગદાન આપવા માટે પણ આમંત્રિત છો. કોઈપણ વપરાશકર્તા ફેરફાર સૂચવી શકે છે અથવા નવા શબ્દો ઉમેરી શકે છે જેથી આ સાઇટ જૈન ધર્મના વિદ્યાર્થીઓ અને વિદ્વાનો માટે વધુ ઉપયોગી બને.\n\nપહેલા અંગ્રેજી સંસ્કરણ તૈયાર કરવા પર ધ્યાન કેન્દ્રિત કરીએ. જૈન શબ્દોની નિશ્ચિત અંગ્રેજી જોડણી વ્યાખ્યાયિત કરવી મહત્વપૂર્ણ છે. હાલમાં આપણે Mahavir, Mahaveer અને Mahavīr જેવા શબ્દો જોઈએ છીએ. દ્યાનુક્ત ચિહ્નો સાથે આપણે નિશ્ચિત જોડણી સ્થાપિત કરી શકીએ.\n\nઆપણે દરેક શબ્દના દેવનાગરી અને ગુજરાતી સંસ્કરણો શામેલ કરવા જોઈએ. ક્રાઉડસોર્સિંગ સક્ષમ થયા પછી, આપણે જનતાને તે શબ્દોની ગુજરાતી અને હિન્દી સમજાવટો સબમિટ કરવા કહી શકીએ જે અંગ્રેજી સમજાવટનું અનિવાર્ય અનુસરણ નથી કરતી.",
    "index.statTerms": "શબ્દો",
    "index.statLanguages": "ભાષાઓ",
    "index.statLanguagesValue": "EN · HI · GU",
    "index.statCommunity": "સમુદાય આધારિત",
    "index.statCommunityValue": "ખુલ્લું",
    "index.ctaTitle": "પવિત્ર જ્ઞાનમાં યોગદાન આપો",
    "index.ctaBody":
      "સર્વસમાવેશક જૈન શબ્દાવલી સંસાધન બનાવવામાં મદદ કરો.",
    "index.startContributing": "યોગદાન શરૂ કરો",
    "index.browseTermsButton": "શબ્દો બ્રાઉઝ કરો",
    "footer.blurb":
      "જૈન શબ્દોને ઉચ્ચારણ માર્ગદર્શન અને હિન્દી તથા ગુજરાતીમાં અર્થ સાથે સમજાવતું મલ્ટિમીડિયા પ્લેટફોર્મ.",
    "footer.quickLinks": "ઝડપી લિંકો",
    "footer.about": "વિશે",
    "footer.linkBrowseTerms": "શબ્દો બ્રાઉઝ કરો",
    "footer.advisorLabel": "શૈક્ષણિક સલાહકાર:",
    "footer.editorLabel": "મુખ્ય સંપાદક:",
    "footer.copyright": "© {{year}} જૈન શબ્દકોશ. સર્વાધિકાર સુરક્ષિત.",
    "footer.orgLogoAlt": "શ્રુત રત્નાકર",
    "footer.developedBy": "વિકસિત:",
    "footer.universalSoftware": "Universal Software",
    "footer.preserving": "આવનારી પેઢીઓ માટે પવિત્ર જ્ઞાનનું સંરક્ષણ",
    "stats.terms": "શબ્દો",
    "stats.languages": "ભાષાઓ",
    "stats.contributors": "યોગદાનકર્તાઓ",
    "contrib.loadFailed": "તમારું યોગદાન લોડ થયું નહીં.",
    "contrib.signInTitle": "તમારી રજૂઆતો જોવા સાઇન ઇન કરો",
    "contrib.signInBody":
      "લોગ ઇન પછી, તમારા બાકી અને સમીક્ષિત શબ્દો અહીં દેખાશે.",
    "contrib.signInButton": "લોગ ઇન",
    "contrib.statTotal": "કુલ",
    "contrib.statPending": "બાકી",
    "contrib.statApproved": "માન્ય",
    "contrib.statRejected": "નકારેલું",
    "contrib.colIndex": "ક્રમ",
    "contrib.colWord": "શબ્દ",
    "contrib.colMeaningEn": "અર્થ (અંગ્રેજી)",
    "contrib.colStatus": "સ્થિતિ",
    "contrib.colSubmitted": "સબમિટ તારીખ",
    "contrib.colEmail": "ઇમેઇલ",
    "contrib.noContributions": "હજી કોઈ યોગદાન નથી",
    "contrib.noContributionsHint":
      "પ્રથમ શબ્દ મોકલવા માટે ઉપર સબમિટ બટનનો ઉપયોગ કરો.",
    "contrib.statusPending": "બાકી",
    "contrib.statusApproved": "માન્ય",
    "contrib.statusRejected": "નકારેલું",
    "form.loginRequired": "લોગ ઇન જરૂરી",
    "form.loginRequiredBody":
      "શબ્દકોશમાં યોગદાન માટે કૃપા કરીને લોગ ઇન કરો અથવા ખાતું બનાવો.",
    "form.loginRegisterButton": "લોગ ઇન / રજીસ્ટર",
    "form.accountDeactivated": "ખાતું નિષ્ક્રિય",
    "form.accountDeactivatedBody":
      "પ્રશાસક દ્વારા તમારું ખાતું નિષ્ક્રિય કરાયું છે. મદદ માટે સંપર્ક કરો.",
    "form.sessionExpired": "સત્ર સમાપ્ત થયું। ફરી સાઇન ઇન કરો.",
    "form.profileLoadFailed":
      "પ્રોફાઇલ લોડ થઈ નહીં. પૃષ્ઠ ફરીથી લોડ કરો.",
    "form.submitFailed": "યોગદાન સબમિટ થયું નહીં.",
    "form.existingTerms": "હાજર શબ્દો",
    "form.viewTerm": "જુઓ →",
    "form.termAlreadyExists":
      "\"{{word}}\" શબ્દકોશમાં પહેલેથી છે.",
    "form.viewExistingTerm": "હાજર શબ્દ જુઓ →",
    "form.submitting": "સબમિટ થઈ રહ્યું છે...",
    "form.validate.wordRequired": "શબ્દ જરૂરી છે",
    "form.validate.nameRequired": "નામ જરૂરી છે",
    "form.validate.emailRequired": "ઇમેઇલ જરૂરી છે",
    "form.validate.emailInvalid": "માન્ય ઇમેઇલ દાખલ કરો",
    "form.validate.oneMeaning":
      "ઓછામાં ઓછો એક અર્થ (અંગ્રેજી, હિન્દી અથવા ગુજરાતી) જરૂરી છે",
    "form.audioLabel": "ઑડિયો જોડાણ",
    "form.audioInvalidType": "કૃપા કરીને ઑડિયો ફાઇલ પસંદ કરો",
    "form.audioPickDifferent": "બીજી ફાઇલ પસંદ કરો",
    "form.audioAttachReplace": "જોડો / બદલો",
    "form.audioRemoveNew": "નવો અપલોડ દૂર કરો",
    "form.audioSaving": "સાચવી રહ્યા છીએ…",
    "form.audioAttach": "ઑડિયો ફાઇલ જોડો",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() =>
    typeof window !== "undefined" ? readStoredLanguage() : "en"
  );

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(JL_LANG_KEY, lang);
    } catch {
      /* noop */
    }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const pack = translations[language] ?? translations.en;
      const fallback = translations.en;
      const raw = pack[key] ?? fallback[key] ?? key;
      return interpolate(raw, vars);
    },
    [language]
  );

  useEffect(() => {
    document.documentElement.lang =
      language === "en" ? "en" : language === "hi" ? "hi" : "gu";
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
